import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Briefcase, Loader } from 'lucide-react';
import { getRecruitmentPositions, createRecruitmentPosition, updateRecruitmentPosition, deleteRecruitmentPosition } from '../../api';
import { useToast } from '../../contexts/NotificationContext';

const RecruitmentPositionManagement = () => {
  const toast = useToast();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await getRecruitmentPositions();
      setPositions(data);
    } catch (e) {
      toast.error('Erreur chargement des postes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await createRecruitmentPosition({ name: newName.trim() });
      setNewName('');
      toast.success('Poste ajouté');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur création');
    }
    setSaving(false);
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      await updateRecruitmentPosition(id, { name: editName.trim() });
      setEditingId(null);
      toast.success('Poste modifié');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur modification');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce poste ?')) return;
    try {
      await deleteRecruitmentPosition(id);
      toast.success('Poste supprimé');
      load();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  if (loading) return <div className="text-center py-8 text-gray-400"><Loader className="animate-spin mx-auto" size={24} /></div>;

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Briefcase size={20} className="text-violet-600" />
        <h2 className="text-lg font-semibold text-gray-800">Postes de recrutement</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Gerez la liste des postes disponibles pour le recrutement. Ces postes seront proposes en selection lors de la creation de candidatures.
      </p>

      {/* Formulaire ajout */}
      <form onSubmit={handleCreate} className="flex gap-2 mb-6">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Nom du poste (ex: Assistant(e) Accueil Petite Enfance)"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-200 focus:border-violet-400 outline-none"
        />
        <button
          type="submit"
          disabled={!newName.trim() || saving}
          className="flex items-center gap-1 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700 disabled:opacity-50 transition"
        >
          {saving ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
          Ajouter
        </button>
      </form>

      {/* Liste */}
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        {positions.length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">Aucun poste configure</div>
        ) : (
          positions.map(pos => (
            <div key={pos.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition">
              {editingId === pos.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleUpdate(pos.id); if (e.key === 'Escape') setEditingId(null); }}
                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-violet-200 outline-none"
                    autoFocus
                  />
                  <button onClick={() => handleUpdate(pos.id)} className="px-2 py-1 text-xs bg-violet-600 text-white rounded">OK</button>
                  <button onClick={() => setEditingId(null)} className="px-2 py-1 text-xs text-gray-500">Annuler</button>
                </div>
              ) : (
                <>
                  <span className="text-sm text-gray-800">{pos.name}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setEditingId(pos.id); setEditName(pos.name); }} className="p-1.5 text-gray-400 hover:text-blue-600 transition" title="Modifier">
                      <Edit size={14} />
                    </button>
                    <button onClick={() => handleDelete(pos.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition" title="Supprimer">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecruitmentPositionManagement;
